<template>
  <el-container class="helper-layout">
    <!-- 左侧导航 -->
    <el-aside :width="collapsed ? '64px' : '260px'" class="sidebar">
      <div class="sidebar-inner">
        <!-- 工作区头部 -->
        <div class="ws-header">
          <div class="ws-toggle">
            <!-- Logo -->
            <div class="navbar-brand">
              <div class="brand-icon" @click="collapsed = !collapsed">
                <img src="@/assets/jenkins.png" alt="Logo" />
              </div>
              <div v-show="!collapsed" @click="$router.push('/')" class="brand-text">
                <div class="brand-name">极客码艺-毕设管家</div>
                <div class="brand-sub">GEEK CODING STUDIO</div>
              </div>
            </div>
          </div>
          <button class="collapse-btn" v-show="!collapsed" @click="collapsed = true">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="16" height="16">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 19l-7-7 7-7m8 14l-7-7 7-7"/>
            </svg>
          </button>
        </div>

        <!-- 导航菜单 -->
        <nav class="sidebar-nav">
          <el-tooltip
            v-for="item in navItems"
            :key="item.path"
            :content="item.label"
            placement="right"
            :disabled="!collapsed"
          >
            <router-link
              :to="item.path"
              :class="['nav-item', collapsed ? 'centered' : '']"
            >
              <!-- 自定义 SVG 图标 -->
              <svg v-if="item.customIcon" fill="none" stroke="currentColor" viewBox="0 0 24 24" class="nav-icon">
                <g v-html="item.customIcon"></g>
              </svg>
              <!-- 普通 path 图标 -->
              <svg v-else fill="none" stroke="currentColor" viewBox="0 0 24 24" class="nav-icon">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" :d="item.icon"/>
              </svg>
              <span v-show="!collapsed">{{ item.label }}</span>
            </router-link>
          </el-tooltip>
        </nav>

        <!-- 底部用户信息栏 -->
        <div class="user-info-bar">
          <!-- 提示卡片 -->
          <div class="tips-card" v-show="!collapsed">
            <!-- 推荐给朋友 -->
            <div class="tip-item tip-text-only">
              <span>如有帮助，可将助手工具推荐给朋友</span>
            </div>
            <!-- 定制服务 -->
            <div class="tip-item tip-text-only">
              <span>定制程序、论文协助可填加微信咨询</span>
            </div>
          </div>

          <!-- 退出登录按钮（仅折叠状态显示） -->
          <el-tooltip content="退出登录" placement="right" :disabled="!collapsed">
            <button v-show="collapsed" class="logout-btn collapsed-logout" @click="handleLogout">
              <i class="el-icon-switch-button"></i>
            </button>
          </el-tooltip>
        </div>
      </div>
    </el-aside>

    <!-- 右侧内容容器 -->
    <el-container class="main-container">
      <!-- 顶部 -->
      <el-header height="55px" class="main-header">
        <div class="header-spacer"></div>
        <div class="header-right">
          <div class="manual-link shake-animation" @click="openManual">
            <i class="el-icon-document"></i>
            <span>查看操作手册</span>
          </div>
        </div>
        <div class="user-points">
          <div class="points-left">
            <img src="@/assets/points.png" alt="积分" />
            <span>剩余积分</span>
          </div>
          <div class="points-right">
            <span class="points-value" :class="{ 'points-change-animation': pointsChanged }">{{ displayPoints }}</span>
            <span class="points-divider">|</span>
            <a href="javascript:void(0)" class="recharge-link-sidebar" @click="showRecharge = true">去充值</a>
          </div>
        </div>
        <el-popover
          placement="bottom"
          trigger="hover"
          popper-class="user-logout-popover">
          <div class="logout-menu" @click="handleRechargeHistory">
            <i class="el-icon-document"></i>
            <span>充值账单</span>
          </div>
          <div class="logout-menu" @click="handleMessageWall">
            <i class="el-icon-chat-line-square"></i>
            <span>留言墙</span>
          </div>
          <div class="logout-menu" @click="handleLogout">
            <i class="el-icon-switch-button"></i>
            <span>退出登录</span>
          </div>
          <div slot="reference" class="user-info-display">
            <span class="user-name">{{ user.username }}</span>
          </div>
        </el-popover>
      </el-header>

      <!-- 主内容区 -->
      <el-main class="main-content">
        <router-view/>
      </el-main>

      <!-- 底部 -->
      <el-footer height="auto" class="main-footer">
        <HelperFooter />
      </el-footer>
    </el-container>

    <!-- 充值模态框 -->
    <RechargeModal :visible.sync="showRecharge" @show-contact="showContact = true" />

    <!-- 快速联系模态框 -->
    <ContactModal :visible.sync="showContact" />

    <!-- 操作手册模态框 -->
    <ManualModal :visible.sync="showManual" />

    <!-- 登录模态框 -->
    <LoginModal
      :visible.sync="showLoginModal"
      @login-success="handleLoginSuccess"
    />
  </el-container>
</template>

<script>
import RechargeModal from "@/components/RechargeModal";
import ContactModal from "@/components/ContactModal";
import ManualModal from "@/components/ManualModal";
import LoginModal from "@/components/LoginModal";
import HelperFooter from "@/components/HelperFooter";

export default {
  name: 'Helper',
  components: { RechargeModal, ContactModal, ManualModal, LoginModal, HelperFooter },
  data() {
    return {
      collapsed: false,
      user: {
        username: '',
        nickname: '',
        points: 0
      },
      displayPoints: 0,
      pointsChanged: false,
      showRecharge: false,
      showContact: false,
      showManual: false,
      showLoginModal: false,
      navItems: [
        {
          path: '/helper/diagram',
          label: 'ER图/三线表生成',
          icon: 'M5 21h14v-5H5v5zM12 16v-5M12 11a3 3 0 100-6 3 3 0 000 6zM7 16l-3-6M4 10a2 2 0 100-4 2 2 0 000 4zM17 16l3-6M20 10a2 2 0 100-4 2 2 0 000 4z'
        },
        {
          path: '/helper/structureChart',
          label: '功能图',
          icon: 'M8 2h8v6H8zM12 8v3M3 11h18M3 11v6h6v-6H3zM9 11v6h6v-6H9zM15 11v6h6v-6h-6z'
        },
        {
          path: '/helper/useCaseDiagram',
          label: '用例图',
          icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z'
        },
        {
          path: '/helper/flowchart',
          label: '流程图',
          icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01'
        },
        {
          path: '/helper/swimlane',
          label: '泳道图',
          icon: 'M4 5a1 1 0 011-1h4a1 1 0 011 1v7a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v7a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 16a1 1 0 011-1h4a1 1 0 011 1v3a1 1 0 01-1 1H5a1 1 0 01-1-1v-3zM14 16a1 1 0 011-1h4a1 1 0 011 1v3a1 1 0 01-1 1h-4a1 1 0 01-1-1v-3z'
        },
        {
          path: '/helper/sequence',
          label: '时序图',
          icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z'
        },
        {
          path: '/helper/taskBook',
          label: '任务书/开题报告',
          icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z'
        },
        {
          path: '/helper/articleGenerate',
          label: '文章生成',
          icon: 'M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z'
        },
        {
          path: '/helper/testCase',
          label: '测试用例',
          icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4'
        },
        {
          path: '/helper/reduceAigc',
          label: '降重降AIGC',
          icon: 'M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z'
        },
        {
          path: '/helper/aigcCheck',
          label: 'AIGC检测',
          customIcon: `
            <!-- 文档外框（右下角完全空开） -->
            <path stroke-width="1.5" fill="none" stroke-linecap="round" stroke-linejoin="round" d="M4 2 h12 a2 2 0 0 1 2 2 v9 M4 20 h7 M4 2 a2 2 0 0 0 -2 2 v14 a2 2 0 0 0 2 2"/>
            <!-- AI 文字 -->
            <text x="6.5" y="12.5" font-size="8.5" font-weight="bold" fill="currentColor" stroke="none">AI</text>
            <!-- 小放大镜 -->
            <circle cx="17" cy="17.5" r="2.5" stroke-width="1.5" fill="none"/>
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M19 19.5l1.5 1.5"/>
          `
        }
      ]
    }
  },
  created() {
    this.loadUser()
    // 使用 nextTick 确保 loadUser 完成后再加载积分
    this.$nextTick(() => {
      this.loadUserPoints()
    })

    // 监听积分更新事件
    this.$root.$on('refresh-points', this.loadUserPoints)
  },
  mounted() {
    // 页面加载后自动显示通知
    this.showNotification()
  },
  beforeDestroy() {
    // 移除事件监听
    this.$root.$off('refresh-points', this.loadUserPoints)
  },
  watch: {
    'user.points'(newVal, oldVal) {
      // 只有在积分真正变化时才触发动画（不包括初始化）
      if (oldVal !== undefined && oldVal !== 0 && newVal !== oldVal) {
        this.animatePoints(oldVal, newVal)
      } else if (oldVal === 0 || oldVal === undefined) {
        // 初始化时直接设置，不触发动画
        this.displayPoints = newVal || 0
      }
    },
    // 监听 store 中的登录弹窗状态
    '$store.state.showLoginModal'(newVal) {
      if (newVal) {
        this.showLoginModal = true
        // 重置 store 中的状态
        this.$store.commit('setShowLoginModal', false)
      }
    }
  },
  methods: {
    // 显示不会自动关闭的通知
    showNotification() {
      // 检查 localStorage 中是否已经关闭过通知
      const notificationClosed = localStorage.getItem('helperNotificationClosed')
      
      if (!notificationClosed) {
        const notifyInstance = this.$notify({
          title: '服务升级通知',
          message: '尊敬的用户，由于AI接口服务成本持续上涨，平台暂停积分赠送活动。感谢您的理解与支持，如有需要可选择充值购买积分继续使用服务。',
          duration: 0,
          onClose: () => {
            // 用户手动关闭时，记录到 localStorage
            localStorage.setItem('helperNotificationClosed', 'true')
          }
        });
      }
    },
    animatePoints(from, to) {
      // 触发放大动画
      this.pointsChanged = true

      const duration = 800 // 总动画时长 800ms
      const steps = 30 // 滚动步数
      const stepDuration = duration / steps
      const increment = (to - from) / steps
      let current = from
      let step = 0

      const timer = setInterval(() => {
        step++
        current += increment
        this.displayPoints = Math.round(current)

        if (step >= steps) {
          clearInterval(timer)
          this.displayPoints = to
          // 动画结束后还原大小
          setTimeout(() => {
            this.pointsChanged = false
          }, 200)
        }
      }, stepDuration)
    },
    loadUser() {
      const userStr = localStorage.getItem('user')
      const userData = userStr ? JSON.parse(userStr) : null
      if (!userData) {
        // 没有用户信息时，触发登录弹窗（不跳转页面）
        this.$store.commit('setShowLoginModal', true)
        return
      }
      // 使用 Object.assign 确保响应式
      Object.assign(this.user, userData)
    },
    async loadUserPoints() {
      if (!this.user || !this.user.username) {
        return
      }
      try {
        const res = await this.request.get(`/user/points/${this.user.username}`)
        if (res.code === '200') {
          // 直接赋值，因为 user 已经是响应式对象
          this.user.points = res.data
          // 同时更新 localStorage
          const userStr = localStorage.getItem('user')
          if (userStr) {
            const userData = JSON.parse(userStr)
            userData.points = res.data
            localStorage.setItem('user', JSON.stringify(userData))
          }
        }
      } catch (error) {
        console.error('获取积分失败:', error)
      }
    },
    openManual() {
      this.showManual = true
    },
    handleLoginSuccess(userData) {
      this.user = userData
      this.showLoginModal = false
      // 登录成功后重新加载积分
      this.$nextTick(() => {
        this.loadUserPoints()
      })
    },
    handleRechargeHistory() {
      // 跳转到充值历史页面
      this.$router.push('/helper/rechargeHistory')
    },
    handleMessageWall() {
      // 跳转到留言墙页面
      this.$router.push('/helper/messageWall')
    },
    handleLogout() {
      this.$confirm('确定要退出登录吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        localStorage.removeItem('user')
        this.$message.success('已退出登录')
        this.$router.push('/home')
      }).catch(() => {})
    }
  }
}
</script>

<style scoped>
/* 整体布局容器 */
.helper-layout {
  height: 100vh;
  background: #f3f4f6;
  overflow: hidden;
}

.helper-layout >>> .el-container {
  height: 100%;
}

/* 侧边栏 */
.sidebar {
  background: #fff;
  border-right: 1px solid #e5e7eb;
  overflow: hidden;
  height: 100vh;
}

.sidebar >>> .el-aside {
  transition: width 0.35s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  overflow: hidden;
}

.sidebar-inner {
  padding: 16px;
  height: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
}

/* 收起状态的样式 */
.el-aside[style*="width: 64px"] .sidebar-inner {
  padding: 12px 8px;
}

.el-aside[style*="width: 64px"] .ws-header {
  justify-content: center;
}

.el-aside[style*="width: 64px"] .ws-toggle {
  justify-content: center;
  padding: 4px;
}

.el-aside[style*="width: 64px"] .user-info-content {
  justify-content: center;
  margin-bottom: 0;
}

.el-aside[style*="width: 64px"] .user-info-bar {
  margin-left: -8px;
  margin-right: -8px;
  margin-bottom: -12px;
  padding: 8px 6px;
}

/* 工作区头部 */
.ws-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  min-height: 36px;
}

.ws-toggle {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  padding: 4px;
  transition: background 0.2s;
  overflow: hidden;
}

.ws-toggle:hover { background: #f9fafb; }

.navbar-brand {
  display: flex;
  align-items: center;
  gap: 10px;
}

.brand-icon {
  width: 38px;
  height: 38px;
  background: #f3f4f6;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;
  cursor: pointer;
  transition: background 0.2s;
}

.brand-icon:hover {
  background: #e5e7eb;
}

.brand-icon img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.brand-text {
  cursor: pointer;
  transition: opacity 0.2s;
}

.brand-text:hover {
  opacity: 0.7;
}

.brand-name {
  font-size: 15px;
  font-weight: 700;
  color: #111827;
  white-space: nowrap;
}

.brand-sub {
  font-size: 10px;
  color: #6b7280;
  letter-spacing: 0.1em;
  font-weight: 700;
  white-space: nowrap;
}

.collapse-btn {
  background: none;
  border: none;
  color: #9ca3af;
  cursor: pointer;
  padding: 6px;
  flex-shrink: 0;
  transition: background 0.2s;
}

.collapse-btn:hover { background: #f3f4f6; color: #374151; }

/* 导航菜单 */
.sidebar-nav {
  display: flex;
  flex-direction: column;
  gap: 3px;
  flex: 1;
  overflow-y: auto;
  min-height: 0;
}

.nav-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 9px 12px;
  font-size: 15px;
  color: #374151;
  text-decoration: none;
  transition: all 0.2s;
  white-space: nowrap;
  overflow: hidden;
  flex-shrink: 0;
  min-height: 47px;
  height: 47px;
}

.nav-item span {
  transition: opacity 0.35s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  white-space: nowrap;
  overflow: hidden;
}

.nav-item:hover { background: #f3f4f6; color: #4f46e5; }
.nav-item.router-link-active { background: #eef2ff; color: #4f46e5; font-weight: normal; }
.nav-item.centered { justify-content: center; padding: 10px; height: 47px; }

.nav-icon { width: 28px; height: 28px; flex-shrink: 0; }

/* 右侧主容器 */
.main-container {
  height: 100vh;
  overflow: hidden;
}

.main-container >>> .el-container {
  height: 100%;
}

/* 顶部 */
.main-header {
  background: #fff;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px;
  margin-bottom: 5px;
}

.header-spacer {
  flex: 1;
}

/* 右侧区域 */
.header-right {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-right: 16px;
}

.manual-link {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  padding: 6px 8px;
  font-size: 13px;
  color: #5a6c7d;
  font-weight: normal;
  cursor: pointer;
  transition: all 0.2s;
  border-radius: 4px;
}

.manual-link:hover {
  background: rgba(255, 255, 255, 0.5);
  color: #2563eb;
}

.manual-link i {
  font-size: 18px;
  color: #3b82f6;
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* 右侧区域 */
.header-right {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-right: 16px;
}

.wechat-tip {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  padding: 6px 8px;
  font-size: 13px;
  color: #5a6c7d;
  font-weight: normal;
  cursor: pointer;
  transition: all 0.2s;
  border-radius: 4px;
}

.wechat-tip:hover {
  background: rgba(255, 255, 255, 0.5);
  color: #2563eb;
}

.wechat-tip i {
  font-size: 18px;
  color: #3b82f6;
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.wechat-tip strong {
  color: #3b82f6;
  font-weight: normal;
}

.wechat-tip:hover strong {
  color: #1d4ed8;
}

.main-header >>> .el-header {
  padding: 0 24px;
  height: 55px !important;
  display: flex;
  align-items: center;
}

.main-header .user-points {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 2px 12px;
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid #8c6ff7;
  border-radius: 6px;
  font-size: 13px;
  color: #3730a3;
  font-weight: 500;
  white-space: nowrap;
  min-height: 32px;
}

.main-header .points-left {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.main-header .points-right {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  min-width: 0;
}

.main-header .user-points img {
  width: 20px;
  height: 20px;
  object-fit: contain;
  flex-shrink: 0;
}

.main-header .points-value {
  font-size: 18px;
  font-weight: 700;
  color: #4f46e5;
  transition: transform 0.3s ease, font-size 0.3s ease;
  display: inline-block;
  min-width: 40px;
  text-align: right;
  flex-shrink: 0;
}

.main-header .points-divider {
  color: #c7d2fe;
  font-size: 14px;
  flex-shrink: 0;
}

.main-header .recharge-link-sidebar {
  font-size: 16px;
  color: #4f46e5;
  text-decoration: none;
  font-weight: 600;
  transition: color 0.2s;
  cursor: pointer;
  flex-shrink: 0;
  white-space: nowrap;
}

.main-header .recharge-link-sidebar:hover {
  color: #4338ca;
}

/* 用户信息展示 */
.user-info-display {
  margin-left: 5px;
  display: flex;
  align-items: center;
  padding: 0px 16px;
  border-radius: 6px;
  min-height: 32px;
  cursor: pointer;
  transition: background 0.2s;
}

.user-info-display:hover {
  background: rgba(170, 197, 250, 0.2);
}

.user-info-display .user-name {
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  white-space: nowrap;
  line-height: 1.5;
}

/* 退出登录菜单 */
.logout-menu {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  cursor: pointer;
  transition: all 0.2s;
  color: #374151;
  font-size: 14px;
}

.logout-menu:not(:last-child) {
  border-bottom: 1px solid #e5e7eb;
}

/* 充值账单悬停效果 */
.logout-menu:nth-child(1):hover {
  background: #f3f4f6 !important;
  color: #6366f1 !important;
}

/* 留言墙悬停效果 */
.logout-menu:nth-child(2):hover {
  background: #f3f4f6 !important;
  color: #6366f1 !important;
}

/* 退出登录悬停效果 */
.logout-menu:nth-child(3):hover {
  background: #fee2e2 !important;
  color: #dc2626 !important;
}

.logout-menu i {
  font-size: 16px;
}

/* 主内容区 */
.main-content {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 0 !important;
}

.main-content >>> .el-main {
  padding: 0 !important;
}

/* 底部 */
.main-footer {
  flex-shrink: 0;
}

.main-footer >>> .el-footer {
  padding: 0;
  height: auto !important;
}

/* 底部用户信息栏 */
.user-info-bar {
  margin-top: auto;
  padding: 10px 8px 0;
  margin-left: -16px;
  margin-right: -16px;
  margin-bottom: -16px;
  padding-bottom: 16px;
}

/* 用户信息卡片 */
.user-info-card {
  background: rgba(255, 255, 255, 0.8);
  border: 1px solid #c7d2fe;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 10px;
}

.user-info-content {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
}

.user-details {
  flex: 1;
  min-width: 0;
}

.user-name {
  font-size: 15px;
  font-weight: 600;
  color: #3730a3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 4px;
}

.user-email {
  font-size: 13px;
  color: #4f46e5;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.user-points {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 12px;
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid #8c6ff7;
  border-radius: 6px;
  font-size: 13px;
  color: #3730a3;
  font-weight: 500;
  white-space: nowrap;
  min-height: 40px;
}

.points-left {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.points-right {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  min-width: 0;
}

.user-points img {
  width: 20px;
  height: 20px;
  object-fit: contain;
  flex-shrink: 0;
}

.points-value {
  font-size: 18px;
  font-weight: 700;
  color: #4f46e5;
  transition: transform 0.3s ease, font-size 0.3s ease;
  display: inline-block;
  min-width: 40px;
  text-align: right;
  flex-shrink: 0;
}

/* 积分变化时的放大动画 */
.points-change-animation {
  animation: pointsScale 1s ease-in-out;
}

@keyframes pointsScale {
  0% {
    transform: scale(1);
  }
  30% {
    transform: scale(1.3);
  }
  70% {
    transform: scale(1.3);
  }
  100% {
    transform: scale(1);
  }
}

.points-divider {
  color: #c7d2fe;
  font-size: 14px;
  flex-shrink: 0;
}

.recharge-link-sidebar {
  font-size: 16px;
  color: #4f46e5;
  text-decoration: none;
  font-weight: 600;
  transition: color 0.2s;
  cursor: pointer;
  flex-shrink: 0;
  white-space: nowrap;
}

.recharge-link-sidebar:hover {
  color: #4338ca;
}

.tips-card {
  padding: 5px;
  border: 1px solid #4f46e5;
  border-radius: 8px;
}

.tip-item {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  padding: 6px 8px;
  font-size: 13px;
  color: #5a6c7d;
  font-weight: normal;
  cursor: pointer;
  transition: all 0.2s;
  border-radius: 4px;
}

.tip-item:hover {
  background: rgba(255, 255, 255, 0.5);
  color: #2563eb;
}

.tip-item i {
  font-size: 18px;
  color: #3b82f6;
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.tip-item strong {
  color: #3b82f6;
  font-weight: normal;
}

.tip-item:hover strong {
  color: #1d4ed8;
}

/* 纯文本提示项 */
.tip-text-only {
  cursor: default;
  font-size: 13px;
  padding: 6px 8px;
  color: #6b7280;
}

.tip-text-only:hover {
  background: transparent;
  color: #6b7280;
}

.logout-btn {
  width: 100%;
  height: 36px;
  background: #fff;
  border: 1px solid #c7d2fe;
  color: #4f46e5;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 14px;
  transition: all 0.2s;
  border-radius: 4px;
}

.logout-btn:hover {
  background: #fee2e2;
  border-color: #fecaca;
  color: #dc2626;
}

.logout-btn i {
  font-size: 16px;
}

/* 抖动动画 */
@keyframes shake {
  0% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-8px);
  }
  100% {
    transform: translateY(0);
  }
}

.shake-animation {
  animation: shake 1s ease-in-out infinite;
}

.shake-animation:hover {
  animation: none;
}

/* 折叠状态下的退出按钮 */
.logout-btn.collapsed-logout {
  background: transparent;
  border: none;
  width: 100%;
  height: auto;
  padding: 8px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.logout-btn.collapsed-logout:hover {
  background: transparent;
  border: none;
}

.logout-btn.collapsed-logout i {
  font-size: 24px;
  margin: 0;
}
</style>
